import React, { useEffect, useState } from 'react';
import classNames from 'classnames/bind';
// eslint-disable-next-line import/no-extraneous-dependencies
import * as Popover from '@radix-ui/react-popover';
import { Text, Toast, IconError16 } from '@stitch-fix/mode-react';
import { KnitLink } from '../../../../KnitLink';
import { getRegion } from '../../../../../i18n';
import DropdownControl from '../../MyAccount/Dropdown/DropdownControl';
import LogoutButton from '../../MyAccount/Dropdown/LogoutButton';
import SignOutModal from './SignOutModal';
import styles from './style.module.scss';
import { Client } from '../../../../NavContextWrapper/types';
import { regionalUrls } from '../../../../../i18n/regionalUrls';
import HouseholdMembers from '../../MyAccount/Dropdown/HouseholdMembers';
import { useHousehold } from '../../helpers/useHousehold';
import { useSignOutModal } from '../../hooks/useSignOutModal';

interface SimplifiedDropdownProps {
  client: Client;
  isPrimaryNavVisible: boolean;
  isNameVisible?: boolean;
}

const cx = classNames.bind(styles);

/**
 * This is a simplified version of the Dropdown menu that contains the Help, Account Settings, Style Profile,
 * and Logout links, as well as the Household Members section with any household accounts
 * that may already exist. The option to add a new profile has recently been removed from this dropdown.
 */
const SimplifiedDropdown = ({
  client,
  isPrimaryNavVisible,
  isNameVisible,
}: SimplifiedDropdownProps) => {
  const siteRegion = getRegion();
  const { getCircleColorForUser, showHouseholdSection } = useHousehold(client);
  const [open, setOpen] = useState(false);

  const { logoutButtonProps, signOutModalProps, errorToastProps } =
    useSignOutModal({
      client,
      onPopoverClose: () => setOpen(false),
    });

  const styleLinks = (
    <KnitLink
      data-ga="meganav-primary-dropdown-style-profile"
      to="/style/profile"
      className={cx('section-row', 'section-row-link')}
    >
      Style Profile
    </KnitLink>
  );

  const accountLinks = (
    <KnitLink
      data-ga="meganav-primary-dropdown-account-settings"
      to="/settings"
      className={cx('section-row', 'section-row-link')}
    >
      Account Settings
    </KnitLink>
  );

  const miscLinks = (
    <>
      <KnitLink
        data-ga="meganav-primary-dropdown-help"
        className={cx('section-row', 'section-row-link')}
        to={regionalUrls[siteRegion].support}
        target="_blank"
        rel="noopener noreferrer"
      >
        Help
      </KnitLink>
      <LogoutButton {...logoutButtonProps} />
    </>
  );

  const linkSections = [
    {
      id: 'account',
      heading: 'Your Account',
      links: (
        <>
          {styleLinks}
          {accountLinks}
          {miscLinks}
        </>
      ),
    },
  ];

  useEffect(() => {
    if (!isPrimaryNavVisible) setOpen(false);
  }, [isPrimaryNavVisible]);

  return (
    <div className={styles['dropdown-control-wrapper']}>
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <DropdownControl
            firstName={client.firstName || ''}
            firstNameColor="dark"
            circleColor={getCircleColorForUser(client)}
            isNameVisible={isNameVisible}
            isOpen={open}
          />
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className={styles['dropdown-wrapper']}
            side="bottom"
            align="end"
            sideOffset={8}
            avoidCollisions={false}
          >
            <div
              className={cx({
                'dropdown-menu': true,
                'dropdown-menu--open': open,
              })}
              data-testid="simplified-dropdown"
              role="menu"
            >
              <div className={styles['name-wrapper']}>
                <DropdownControl
                  firstName={client.firstName || ''}
                  theme="light"
                  onClick={() => setOpen(false)}
                  circleColor={getCircleColorForUser(client)}
                  isOpen={open}
                />
              </div>

              {showHouseholdSection && (
                <div
                  className={styles.section}
                  data-testid="dropdown-menu-household-section"
                >
                  <HouseholdMembers client={client} />
                </div>
              )}

              {linkSections.map(
                section =>
                  section && (
                    <div key={section.id}>
                      <div
                        className={styles.section}
                        data-testid={`dropdown-menu-${section.id}-section`}
                      >
                        {section.heading && (
                          <Text setting="eyebrow-small" ml={0.375} my={0.5}>
                            {section.heading}
                          </Text>
                        )}
                        {section.links}
                      </div>
                    </div>
                  ),
              )}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {/* Render modal outside of Popover so it's not unmounted when popover closes */}
      <SignOutModal {...signOutModalProps} />
      <Toast
        {...errorToastProps}
        startContent={<IconError16 purpose="decorative" />}
      >
        We couldn&apos;t sign you out. Please try again.
      </Toast>
    </div>
  );
};

export default SimplifiedDropdown;
